import { Injectable } from '@angular/core';
import { MerchantService } from '@galaxy/billing';
import { Category, FieldMask, StoreService } from '@vendasta/marketplace-packages';
import { forkJoin, Observable, of as observableOf } from 'rxjs';
import { map } from 'rxjs/operators';
import { Currency } from '../../core/types';
import { allId, StoreCategory } from './category';
import { Store } from './store';

export interface CategoryIdState {
  categoryId: string;
  state: boolean;
}

export interface RetailConfiguration {
  currency: Currency;
  currencyConversionRate: number;
}

@Injectable({ providedIn: 'root' })
export class ManageStoreService {
  constructor(public storeService: StoreService, private merchantService: MerchantService) {}

  private storeCategoryFromAPICategory(cat: Category): StoreCategory {
    if (!cat || !cat.storeId) {
      return null;
    }
    return {
      partnerId: cat.storeId.partnerId,
      marketId: cat.storeId.marketId,
      categoryId: cat.categoryId,
      name: cat.categoryName || '',
      packageIds: cat.packageIds || [],
      showTabInStore: cat.visibleInStore || false,
    };
  }

  public getStore(partnerId: string, marketId: string): Observable<Store> {
    return this.storeService.getStore(partnerId, marketId).pipe(
      map((resp) => {
        return {
          partnerId: resp.storeId.partnerId,
          marketId: resp.storeId.marketId,
          useCustomCategories: resp.useCustomCategories,
          categoryOrder: resp.categoryOrder,
        };
      }),
    );
  }

  public saveUseCustomCategories(
    partnerId: string,
    marketId: string,
    useCustomCategories: boolean,
  ): Observable<unknown> {
    const fieldMask = new FieldMask({
      paths: ['useCustomCategories'],
    });
    return this.storeService.updateStore(partnerId, marketId, null, useCustomCategories, fieldMask);
  }

  public saveCategoryOrder(partnerId: string, marketId: string, categoryOrder: StoreCategory[]): Observable<unknown> {
    const categoryIds = categoryOrder.map((cat) => cat.categoryId);
    return this.storeService.setStoreCategoryOrder(partnerId, marketId, categoryIds);
  }

  public createCategory(partnerId: string, marketId: string, categoryName: string): Observable<StoreCategory> {
    return this.storeService
      .createStoreCategory(partnerId, marketId, categoryName)
      .pipe(map((category) => this.storeCategoryFromAPICategory(category)));
  }

  public deleteCategory(partnerId: string, marketId: string, categoryId: string): Observable<unknown> {
    return this.storeService.deleteStoreCategory(partnerId, marketId, categoryId);
  }

  public getCategories(partnerId: string, marketId: string): Observable<StoreCategory[]> {
    return this.storeService
      .getCategoriesForStore(partnerId, marketId)
      .pipe(
        map((categories) => (categories || []).map((cat) => this.storeCategoryFromAPICategory(cat)).filter(Boolean)),
      );
  }

  public saveCategoryName(partnerId: string, marketId: string, categoryId: string, name: string): Observable<unknown> {
    const fieldMask = new FieldMask({
      paths: ['name'],
    });
    return this.storeService.updateStoreCategory(partnerId, marketId, categoryId, name, null, fieldMask);
  }

  public saveShowCategoryInStoreState(
    partnerId: string,
    marketId: string,
    categoryId: string,
    state: boolean,
  ): Observable<unknown> {
    const fieldMask = new FieldMask({
      paths: ['visibleInStore'],
    });
    return this.storeService.updateStoreCategory(partnerId, marketId, categoryId, null, state, fieldMask);
  }

  public saveMultiShowCategoryInStoreState(
    partnerId: string,
    marketId: string,
    idStatePairs: CategoryIdState[],
  ): Observable<unknown> {
    const fieldMask = new FieldMask({
      paths: ['visibleInStore'],
    });
    const responses = idStatePairs.map((idState) =>
      this.storeService.updateStoreCategory(partnerId, marketId, idState.categoryId, null, idState.state, fieldMask),
    );
    return forkJoin(responses);
  }

  public listCategoriesForPackage(partnerId: string, marketId: string, packageId: string): Observable<StoreCategory[]> {
    return this.storeService.listCategoriesForPackage(partnerId, marketId, packageId).pipe(
      map((categories) => (categories ? categories : [])),
      map((categories) => categories.map((cat) => this.storeCategoryFromAPICategory(cat))),
    );
  }

  public setPackagesInCategory(
    partnerId: string,
    marketId: string,
    categoryId: string,
    packageIds: string[],
  ): Observable<unknown> {
    if (categoryId === allId) {
      return observableOf({});
    }
    return this.storeService.setPackagesInCategory(partnerId, marketId, packageIds, categoryId);
  }

  public setCategoriesForPackage(
    partnerId: string,
    marketId: string,
    packageId: string,
    categoryIds: string[],
  ): Observable<unknown> {
    return this.storeService.setCategoriesForPackage(partnerId, marketId, packageId, categoryIds);
  }

  public getRetailConfiguration(partnerId: string, marketId: string): Observable<RetailConfiguration> {
    return this.merchantService.getMultiRetailConfigurations(partnerId, [marketId]).pipe(
      map((retailConfigs) => {
        const marketRetailConfig = retailConfigs.get(marketId);
        return {
          currency: marketRetailConfig.currencyCode,
          currencyConversionRate: marketRetailConfig.currencyConversionRate,
        } as RetailConfiguration;
      }),
    );
  }

  public saveRetailConfiguration(
    partnerId: string,
    marketId: string,
    currency: Currency,
    conversionRate: number,
  ): Observable<unknown> {
    return this.merchantService.upsertRetailConfiguration(partnerId, marketId, currency, conversionRate);
  }
}
